
import { useEffect, useState } from 'react';
import Button from '../../components/base/Button';

export default function OperationsPage() {
  const [isVisible, setIsVisible] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    setIsVisible(true);
    
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVisibleSections(prev => new Set(prev).add(entry.target.id));
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const scrollToOperations = () => {
    const operationsSection = document.getElementById('operations-overview');
    if (operationsSection) {
      operationsSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const operations = [
    {
      title: 'Export Coal',
      location: 'Near Richards Bay',
      description: 'Export-grade thermal coal production for international markets with strategic proximity to major shipping routes.',
      currentCapacity: '10,000 tons/month',
      targetCapacity: '200,000 tons/month',
      features: ['High-grade thermal coal', 'Export quality standards', 'Strategic location', 'International markets'],
      icon: 'ri-fire-line',
      image: 'https://public.readdy.ai/ai/img_res/86763a6a920f569f75a3c54afad13b90.jpg'
    },
    {
      title: 'High-Purity Chrome',
      location: 'North West Region',
      description: 'Premium chrome ore production meeting international metallurgical standards for steel and alloy manufacturing.',
      currentCapacity: 'Scaling up',
      targetCapacity: '50,000 tons/month',
      features: ['High-purity ore', 'Metallurgical grade', 'Consistent quality', 'Global demand'],
      icon: 'ri-copper-coin-line',
      image: 'https://readdy.ai/api/search-image?query=Chrome%20ore%20mining%20operation%2C%20metallic%20chrome%20mineral%20extraction%2C%20modern%20mining%20facility%20with%20processing%20equipment%2C%20industrial%20mining%20site%20in%20South%20Africa&width=600&height=400&seq=chrome-mining&orientation=landscape'
    },
    {
      title: 'Iron Ore & Contracting',
      location: 'Emerging Assets',
      description: 'Developing iron ore assets while providing specialized mining contracting services across the sector.',
      currentCapacity: 'Development phase',
      targetCapacity: 'Full production',
      features: ['Emerging assets', 'Contracting services', 'Sector expertise', 'Scalable operations'],
      icon: 'ri-hammer-line',
      image: 'https://readdy.ai/api/search-image?query=Iron%20ore%20mining%20operation%2C%20red%20iron%20ore%20extraction%20site%2C%20heavy%20mining%20machinery%20and%20trucks%2C%20professional%20mining%20photography%2C%20South%20African%20mining%20landscape&width=600&height=400&seq=iron-mining&orientation=landscape'
    }
  ];

  const advantages = [
    {
      title: 'Transformative Ownership',
      description: '100% youth & woman ownership driving innovation',
      icon: 'ri-team-line'
    },
    {
      title: 'Strategic Location',
      description: 'Prime positioning near key infrastructure',
      icon: 'ri-map-pin-line'
    },
    {
      title: 'Quality Assurance',
      description: 'Consistent product quality and standards',
      icon: 'ri-award-line'
    },
    {
      title: 'Scalable Production',
      description: 'Flexible operations meeting market demands',
      icon: 'ri-line-chart-line'
    },
    {
      title: 'Diversified Portfolio',
      description: 'Multiple mineral streams reducing risk',
      icon: 'ri-pie-chart-line'
    },
    {
      title: 'Community Integration',
      description: 'Local partnerships and development focus',
      icon: 'ri-community-line'
    }
  ];

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      {/* Hero Section */}
      <section 
        className="relative h-screen flex items-center justify-center bg-cover bg-center bg-fixed overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(11, 29, 46, 0.8), rgba(11, 29, 46, 0.8)), url('https://readdy.ai/api/search-image?query=Modern%20mining%20operations%20in%20South%20Africa%2C%20large-scale%20mining%20equipment%2C%20open%20pit%20mine%20with%20industrial%20machinery%2C%20professional%20mining%20photography%2C%20dramatic%20lighting%2C%20high%20quality%20corporate%20image&width=1920&height=1080&seq=operations-hero&orientation=landscape')`
        }}
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <h1 
            className={`text-5xl md:text-7xl font-bold text-[#2DD4BF] mb-6 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ fontFamily: '"Montserrat", sans-serif' }}
          >
            Mineral Production Excellence
          </h1>
          <p 
            className={`text-xl md:text-2xl text-[#E9E3D0] mb-8 transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            } leading-relaxed`}
            style={{ fontFamily: '"Open Sans", sans-serif' }}
          >
            Diversified mining operations across South Africa delivering export-grade coal, high-purity chrome, and emerging iron ore through advanced extraction technologies.
          </p>
          <div 
            className={`transition-all duration-1000 delay-500 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <button
              onClick={scrollToOperations}
              className="bg-[#2DD4BF] text-[#0B1D2E] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#2DD4BF]/90 hover:transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
              style={{ fontFamily: '"Montserrat", sans-serif' }}
            >
              Learn More
            </button>
          </div>
        </div>
      </section>

      {/* Operations Overview */}
      <section id="operations-overview" className="py-20 bg-[#F9F9F9]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div 
            id="operations-header"
            data-animate
            className={`text-center mb-16 transition-all duration-1000 ease-out ${
              visibleSections.has('operations-header') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            <h2 
              className="text-4xl md:text-5xl font-bold text-[#0B1D2E] mb-6"
              style={{ fontFamily: '"Montserrat", sans-serif' }}
            >
              Mineral Production Excellence
            </h2>
            <p 
              className="text-xl text-[#0B1D2E] max-w-3xl mx-auto"
              style={{ fontFamily: '"Open Sans", sans-serif' }}
            >
              Strategic operations across coal, chrome, and iron ore sectors
            </p>
          </div>

          <div className="space-y-20">
            {operations.map((operation, index) => (
              <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div 
                  id={`operation-text-${index}`}
                  data-animate
                  className={`${index % 2 === 1 ? 'lg:col-start-2' : ''} transition-all duration-1000 ease-out ${
                    visibleSections.has(`operation-text-${index}`) 
                      ? 'opacity-100 translate-x-0' 
                      : `opacity-0 ${index % 2 === 1 ? 'translate-x-10' : '-translate-x-10'}`
                  }`}
                >
                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 bg-[#2DD4BF] rounded-full flex items-center justify-center mr-4">
                      <i className={`${operation.icon} text-2xl text-[#0B1D2E]`}></i>
                    </div>
                    <div>
                      <h3 
                        className="text-3xl font-bold text-[#0B1D2E]"
                        style={{ fontFamily: '"Montserrat", sans-serif' }}
                      >
                        {operation.title}
                      </h3>
                      <p 
                        className="text-[#14B8A6] font-semibold"
                        style={{ fontFamily: '"Open Sans", sans-serif' }}
                      >
                        {operation.location}
                      </p>
                    </div>
                  </div>
                  
                  <p 
                    className="text-lg text-[#0B1D2E] mb-6 leading-relaxed"
                    style={{ fontFamily: '"Open Sans", sans-serif' }}
                  >
                    {operation.description}
                  </p>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-[#E9E3D0] p-4 rounded-lg">
                      <p className="text-sm text-[#0B1D2E] font-semibold mb-1">Current Capacity</p>
                      <p className="text-lg text-[#14B8A6] font-bold">{operation.currentCapacity}</p>
                    </div>
                    <div className="bg-[#2DD4BF] p-4 rounded-lg">
                      <p className="text-sm text-[#0B1D2E] font-semibold mb-1">Target Capacity</p>
                      <p className="text-lg text-[#0B1D2E] font-bold">{operation.targetCapacity}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-8">
                    {operation.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center">
                        <i className="ri-check-line text-[#2DD4BF] mr-2"></i>
                        <span className="text-sm text-[#0B1D2E]" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>

                  <Button 
                    variant="primary" 
                    size="lg" 
                    className="bg-[#2DD4BF] text-[#0B1D2E] hover:bg-[#14B8A6] hover:transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
                    style={{ fontFamily: '"Montserrat", sans-serif' }}
                  >
                    Learn More
                  </Button>
                </div>

                <div 
                  id={`operation-image-${index}`}
                  data-animate
                  className={`${index % 2 === 1 ? 'lg:col-start-1' : ''} transition-all duration-1000 ease-out delay-300 ${
                    visibleSections.has(`operation-image-${index}`) 
                      ? 'opacity-100 translate-x-0' 
                      : `opacity-0 ${index % 2 === 1 ? '-translate-x-10' : 'translate-x-10'}`
                  }`}
                >
                  <div className="relative">
                    <img 
                      src={operation.image}
                      alt={operation.title}
                      className="w-full h-96 object-cover rounded-2xl shadow-2xl hover:transform hover:scale-105 transition-all duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B1D2E]/50 to-transparent rounded-2xl"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Competitive Advantages */}
      <section className="py-20 bg-[#0B1D2E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div 
            id="advantages-header"
            data-animate
            className={`text-center mb-16 transition-all duration-1000 ease-out ${
              visibleSections.has('advantages-header') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            <h2 
              className="text-4xl md:text-5xl font-bold text-[#2DD4BF] mb-6"
              style={{ fontFamily: '"Montserrat", sans-serif' }}
            >
              Our Competitive Edge
            </h2>
            <p 
              className="text-xl text-[#E9E3D0] max-w-3xl mx-auto"
              style={{ fontFamily: '"Open Sans", sans-serif' }}
            >
              What sets Hera Minerals apart in the mining industry
            </p>
          </div>

          <div 
            id="advantages-grid"
            data-animate
            className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 transition-all duration-1000 ease-out delay-300 ${
              visibleSections.has('advantages-grid') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            {advantages.map((advantage, index) => (
              <div key={index} className="bg-[#E9E3D0]/10 backdrop-blur-sm p-8 rounded-xl border border-[#2DD4BF]/20 hover:border-[#2DD4BF]/50 transition-all duration-500 hover:transform hover:scale-105">
                <div className="w-16 h-16 bg-[#2DD4BF] rounded-full flex items-center justify-center mb-6">
                  <i className={`${advantage.icon} text-2xl text-[#0B1D2E]`}></i>
                </div>
                <h3 
                  className="text-xl font-bold text-[#2DD4BF] mb-4"
                  style={{ fontFamily: '"Montserrat", sans-serif' }}
                >
                  {advantage.title}
                </h3>
                <p 
                  className="text-[#E9E3D0] leading-relaxed"
                  style={{ fontFamily: '"Open Sans", sans-serif' }}
                >
                  {advantage.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Growth Targets */}
      <section className="py-20 bg-[#E9E3D0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div 
            id="growth-header"
            data-animate
            className={`text-center mb-16 transition-all duration-1000 ease-out ${
              visibleSections.has('growth-header') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            <h2 
              className="text-4xl md:text-5xl font-bold text-[#0B1D2E] mb-6"
              style={{ fontFamily: '"Montserrat", sans-serif' }}
            >
              Future Growth Targets
            </h2>
            <p 
              className="text-xl text-[#0B1D2E] max-w-3xl mx-auto"
              style={{ fontFamily: '"Open Sans", sans-serif' }}
            >
              Ambitious expansion plans driving sustainable growth
            </p>
          </div>

          <div 
            id="growth-stats"
            data-animate
            className={`grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 transition-all duration-1000 ease-out delay-300 ${
              visibleSections.has('growth-stats') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="bg-white p-8 rounded-2xl shadow-xl text-center hover:shadow-2xl transition-all duration-500 hover:transform hover:scale-105">
              <div className="w-20 h-20 bg-[#2DD4BF] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-fire-line text-3xl text-[#0B1D2E]"></i>
              </div>
              <h3 
                className="text-2xl font-bold text-[#0B1D2E] mb-4"
                style={{ fontFamily: '"Montserrat", sans-serif' }}
              >
                Coal Production
              </h3>
              <div className="text-4xl font-bold text-[#14B8A6] mb-2">200,000</div>
              <p className="text-[#0B1D2E]" style={{ fontFamily: '"Open Sans", sans-serif' }}>tons/month target</p>
              <div className="mt-4 bg-[#E9E3D0] rounded-full h-2">
                <div className="bg-[#2DD4BF] h-2 rounded-full w-1/4"></div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-xl text-center hover:shadow-2xl transition-all duration-500 hover:transform hover:scale-105">
              <div className="w-20 h-20 bg-[#14B8A6] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-copper-coin-line text-3xl text-white"></i>
              </div>
              <h3 
                className="text-2xl font-bold text-[#0B1D2E] mb-4"
                style={{ fontFamily: '"Montserrat", sans-serif' }}
              >
                Chrome Production
              </h3>
              <div className="text-4xl font-bold text-[#14B8A6] mb-2">50,000</div>
              <p className="text-[#0B1D2E]" style={{ fontFamily: '"Open Sans", sans-serif' }}>tons/month target</p>
              <div className="mt-4 bg-[#E9E3D0] rounded-full h-2">
                <div className="bg-[#14B8A6] h-2 rounded-full w-1/3"></div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-xl text-center hover:shadow-2xl transition-all duration-500 hover:transform hover:scale-105">
              <div className="w-20 h-20 bg-[#0B1D2E] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-building-line text-3xl text-[#2DD4BF]"></i>
              </div>
              <h3 
                className="text-2xl font-bold text-[#0B1D2E] mb-4"
                style={{ fontFamily: '"Montserrat", sans-serif' }}
              >
                Infrastructure
              </h3>
              <div className="text-4xl font-bold text-[#14B8A6] mb-2">Major</div>
              <p className="text-[#0B1D2E]" style={{ fontFamily: '"Open Sans", sans-serif' }}>investments planned</p>
              <div className="mt-4 bg-[#E9E3D0] rounded-full h-2">
                <div className="bg-[#0B1D2E] h-2 rounded-full w-1/2"></div>
              </div>
            </div>
          </div>

          <div 
            id="infrastructure-details"
            data-animate
            className={`bg-white p-12 rounded-2xl shadow-xl transition-all duration-1000 ease-out delay-500 ${
              visibleSections.has('infrastructure-details') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            <h3 
              className="text-3xl font-bold text-[#0B1D2E] mb-6 text-center"
              style={{ fontFamily: '"Montserrat", sans-serif' }}
            >
              Infrastructure Development
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h4 
                  className="text-xl font-semibold text-[#14B8A6] mb-4"
                  style={{ fontFamily: '"Montserrat", sans-serif' }}
                >
                  Planned Investments
                </h4>
                <ul className="space-y-3" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                  <li className="flex items-center">
                    <i className="ri-check-line text-[#2DD4BF] mr-3"></i>
                    Modern wash plants for coal processing
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-[#2DD4BF] mr-3"></i>
                    Advanced logistics facilities
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-[#2DD4BF] mr-3"></i>
                    Transportation infrastructure
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-[#2DD4BF] mr-3"></i>
                    Environmental monitoring systems
                  </li>
                </ul>
              </div>
              <div>
                <h4 
                  className="text-xl font-semibold text-[#14B8A6] mb-4"
                  style={{ fontFamily: '"Montserrat", sans-serif' }}
                >
                  Strategic Benefits
                </h4>
                <ul className="space-y-3" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                  <li className="flex items-center">
                    <i className="ri-arrow-right-line text-[#2DD4BF] mr-3"></i>
                    Increased production capacity
                  </li>
                  <li className="flex items-center">
                    <i className="ri-arrow-right-line text-[#2DD4BF] mr-3"></i>
                    Enhanced product quality
                  </li>
                  <li className="flex items-center">
                    <i className="ri-arrow-right-line text-[#2DD4BF] mr-3"></i>
                    Improved operational efficiency
                  </li>
                  <li className="flex items-center">
                    <i className="ri-arrow-right-line text-[#2DD4BF] mr-3"></i>
                    Reduced environmental impact
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#2DD4BF]">
        <div 
          id="operations-cta"
          data-animate
          className={`max-w-4xl mx-auto px-6 lg:px-8 text-center transition-all duration-1000 ease-out ${
            visibleSections.has('operations-cta') 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 
            className="text-4xl md:text-5xl font-bold text-[#0B1D2E] mb-6"
            style={{ fontFamily: '"Montserrat", sans-serif' }}
          >
            Partner with Excellence
          </h2>
          <p 
            className="text-xl text-[#0B1D2E] mb-8"
            style={{ fontFamily: '"Open Sans", sans-serif' }}
          >
            Discover opportunities in South Africa's premier mining operations
          </p>
          <div className="flex justify-center">
            <Button 
              variant="secondary" 
              size="lg" 
              href="/contact"
              className="bg-[#0B1D2E] text-white hover:bg-[#0B1D2E]/90 hover:transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
              style={{ fontFamily: '"Montserrat", sans-serif' }}
            >
              Get in Touch
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
